import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner,
} from '@/components/ui';
import { Combobox } from '@/components/ui/combobox';
import { ApiErrorDisplay } from '@/components/features/error';
import { ApiError } from '@/lib/api/client';
import { useMerchants } from '@/hooks/use-merchants';
import {
  useCreateIqServiceInventoryItem,
  useIqServiceInventoryItem,
  useUpdateIqServiceInventoryItem,
} from '@/hooks/use-iq-service-inventory';
import { getIqServiceInventoryItem } from '@/lib/api/iq-service-inventory';
import type { IqServiceInventoryItem, PlatformIqInventoryRow } from '@/types/iq-service-inventory';
import {
  IqServiceInventoryFields,
  emptyUpsertValues,
  isUpsertFormValid,
  upsertValuesFromItem,
  type IqServiceUpsertValues,
} from './iq-service-inventory-fields';
import { toCreateInput, toUpdateInput } from './iq-service-inventory-upsert-mapping';

interface PlatformIqServiceUpsertSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /** Edit only: the row being edited — the merchant is pinned from here. */
  row?: PlatformIqInventoryRow | null;
  /** Edit only: resolved merchant name for the pinned display + title (the page supplies it). */
  merchantName?: string;
}

/**
 * Right-anchored drawer for registering / editing IQ-service keys directly on the
 * platform overview (Spec 356). The Sheet keeps the table visible beside it.
 *
 * - `create`: a prominent REQUIRED merchant picker on top (cross-merchant error-
 *   prevention), then the shared field set. The merchant is just the path parameter of
 *   the reused per-merchant `POST` route.
 * - `edit`: the merchant is PINNED from the row (read-only, no picker). The overview row
 *   carries no `updatedAt`, so the drawer opens immediately in a loading state and fetches
 *   the full record by-key for the optimistic-lock token before prefilling. On a 409 the
 *   fresh record is reloaded (new `updatedAt`), re-prefilled, and the drawer stays open —
 *   no auto-retry.
 *
 * Refresh after a write is handled by the shared mutation hooks (Weg A): they invalidate
 * the platform-overview aggregate, so the table reloads.
 */
export function PlatformIqServiceUpsertSheet({
  open,
  onOpenChange,
  mode,
  row,
  merchantName,
}: PlatformIqServiceUpsertSheetProps) {
  const { t } = useTranslation('iq-service-inventory');

  const [values, setValues] = useState<IqServiceUpsertValues>(emptyUpsertValues);
  const [pickedMerchantId, setPickedMerchantId] = useState('');
  const [merchantError, setMerchantError] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Edit prefill source (carries the optimistic-lock updatedAt). Set from the by-key
  // fetch on first load and replaced on a 409 reload.
  const [initial, setInitial] = useState<IqServiceInventoryItem | null>(null);

  // create: active merchants for the picker. The fetch is gated so it ONLY fires for the
  // open register drawer — not the edit instance, not while closed.
  const { data: merchantsData, isLoading: merchantsLoading } = useMerchants(
    { status: 'active', limit: 100 },
    { enabled: open && mode === 'create' },
  );
  // edit: by-key prefetch for the optimistic-lock updatedAt (the overview row lacks it).
  // Disabled for the create instance (no row) and while closed (the hook gates on the ids).
  const editItemQuery = useIqServiceInventoryItem(row?.merchantId, row?.iqServiceKey);

  const createMutation = useCreateIqServiceInventoryItem(pickedMerchantId);
  const updateMutation = useUpdateIqServiceInventoryItem(
    row?.merchantId ?? '',
    row?.iqServiceKey ?? '',
  );

  const patch = (p: Partial<IqServiceUpsertValues>) => setValues((v) => ({ ...v, ...p }));

  const resolveError = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 409) {
        return mode === 'create' ? t('upsert.error.duplicate') : t('upsert.error.staleLock');
      }
      if (err.status === 422) return t('upsert.error.invalidTransition');
      if (err.status === 400) return err.message || t('upsert.error.validation');
    }
    return err instanceof Error && err.message ? err.message : t('upsert.error.generic');
  };

  // Reset transient state on every open/close. For edit, clearing `initial` lets the
  // by-key fetch re-capture the fresh record for the new row.
  useEffect(() => {
    setValues(emptyUpsertValues());
    setPickedMerchantId('');
    setInitial(null);
    setMerchantError(false);
    setSubmitError(null);
    setPending(false);
  }, [open]);

  // edit: capture the fetched record as the prefill source once per open. The 409 reload
  // overrides `initial` manually, so we only auto-set while it is still null.
  useEffect(() => {
    if (mode === 'edit' && open && initial === null && editItemQuery.data) {
      setInitial(editItemQuery.data);
    }
  }, [mode, open, initial, editItemQuery.data]);

  // edit: (re-)prefill the form whenever the prefill source changes (first load + 409 reload).
  useEffect(() => {
    if (mode === 'edit' && initial) setValues(upsertValuesFromItem(initial));
  }, [mode, initial]);

  // ----- EDIT -----
  if (mode === 'edit') {
    const pinnedMerchant = merchantName ?? row?.merchantId ?? '';
    const canSubmit = !!initial && isUpsertFormValid(values, 'edit') && !pending;

    const submitEdit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!initial || pending || !isUpsertFormValid(values, 'edit')) return;
      setSubmitError(null);
      setPending(true);
      try {
        await updateMutation.mutateAsync(
          toUpdateInput(
            {
              ...values,
              iqServiceKey: values.iqServiceKey.trim(),
              displayName: values.displayName.trim(),
              description: values.description.trim(),
              internetqEmailRef: values.internetqEmailRef.trim(),
            },
            initial,
          ),
        );
        toast.success(t('upsert.editSuccess'));
        onOpenChange(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && row) {
          // Optimistic-lock conflict: reload the fresh record (fresh updatedAt), re-prefill,
          // keep the drawer open. No auto-retry — the operator re-applies and saves again.
          try {
            const fresh = await getIqServiceInventoryItem(row.merchantId, row.iqServiceKey);
            setInitial(fresh);
          } catch {
            // best-effort reload; leave the form as-is on a failed re-fetch
          }
          toast.error(t('upsert.error.staleLock'));
        } else {
          setSubmitError(resolveError(err));
        }
      } finally {
        setPending(false);
      }
    };

    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('overview.crud.editTitle', { merchant: pinnedMerchant })}</SheetTitle>
            <SheetDescription>{t('upsert.description')}</SheetDescription>
          </SheetHeader>

          {editItemQuery.isError ? (
            // Pre-fetch failed: show the error + retry, no form (no premature submit).
            <div className="py-6">
              <ApiErrorDisplay error={editItemQuery.error} onRetry={() => editItemQuery.refetch()} />
            </div>
          ) : !initial ? (
            // Opened immediately in a loading state (no dead-click) while the by-key fetch runs.
            <div className="flex flex-1 items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <form onSubmit={submitEdit} className="flex flex-1 flex-col gap-4">
              <div className="grid gap-1.5">
                <Label>{t('overview.crud.merchantPicker.label')}</Label>
                <p className="text-sm font-medium">{pinnedMerchant}</p>
              </div>

              <IqServiceInventoryFields
                values={values}
                onChange={patch}
                mode="edit"
                initialStatus={initial.status}
              />

              {submitError && (
                <p role="alert" className="text-sm text-destructive">
                  {submitError}
                </p>
              )}

              <SheetFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={pending}
                >
                  {t('upsert.cancel')}
                </Button>
                <Button type="submit" disabled={!canSubmit}>
                  {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('upsert.submitEdit')}
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  // ----- CREATE -----
  const merchantOptions = (merchantsData?.data ?? []).map((m) => ({
    value: m.merchantId,
    label: m.name,
    // Customer number in `description` so the picker is searchable by it (Combobox
    // indexes label+value+description); fall back to merchantId so search never breaks.
    description: m.customerId ?? m.merchantId,
  }));
  const merchantsTruncated = merchantsData?.pagination.hasMore ?? false;

  // The merchant requirement is enforced as a submit-time inline error (Error-Prevention,
  // Spec §3) rather than a disabled button, so the operator learns WHY the submit was held.
  // The button itself is gated only on form validity (key pattern / lengths) + pending.
  const canSubmit = isUpsertFormValid(values, 'create') && !pending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    if (pickedMerchantId === '') {
      setMerchantError(true);
      return;
    }
    if (!isUpsertFormValid(values, 'create')) return;
    setSubmitError(null);
    setPending(true);
    try {
      await createMutation.mutateAsync(
        toCreateInput({
          ...values,
          iqServiceKey: values.iqServiceKey.trim(),
          displayName: values.displayName.trim(),
          description: values.description.trim(),
          internetqEmailRef: values.internetqEmailRef.trim(),
        }),
      );
      toast.success(t('upsert.createSuccess'));
      onOpenChange(false);
    } catch (err) {
      setSubmitError(resolveError(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t('overview.crud.registerTitle')}</SheetTitle>
          <SheetDescription>{t('upsert.description')}</SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="flex flex-1 flex-col gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="iqinv-merchant">
              {t('overview.crud.merchantPicker.label')}{' '}
              <span className="text-destructive">*</span>
            </Label>
            <Combobox
              id="iqinv-merchant"
              options={merchantOptions}
              value={pickedMerchantId}
              onChange={(v) => {
                setPickedMerchantId(v);
                if (v) setMerchantError(false);
              }}
              placeholder={t('overview.crud.merchantPicker.placeholder')}
              searchPlaceholder={t('overview.crud.merchantPicker.searchPlaceholder')}
              emptyText={t('overview.crud.merchantPicker.empty')}
              loading={merchantsLoading}
              error={merchantError}
            />
            {merchantsTruncated && (
              <p className="text-xs text-muted-foreground">
                {t('overview.crud.merchantPicker.truncated')}
              </p>
            )}
            {merchantError && (
              <p className="text-xs text-destructive">
                {t('overview.crud.merchantPicker.required')}
              </p>
            )}
          </div>

          <IqServiceInventoryFields values={values} onChange={patch} mode="create" />

          {submitError && (
            <p role="alert" className="text-sm text-destructive">
              {submitError}
            </p>
          )}

          <SheetFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t('upsert.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('upsert.submitCreate')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
